// src/Components/Filter/FilterInfo.js

import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';

// Icons
import HelpIcon from '@mui/icons-material/Help'; // Question mark in circle
import CloseIcon from '@mui/icons-material/Close'; // The X icon

// Utils
import InfoFilterInputs from '../../utils/InfoFilterInputs';

export default function FilterInfo({
  filterState,
  actions,
  showInfoFilter,
  activeSubmenu,
  canUpdateMarkers,
  allBatches,
}) {
  const FIELD_WIDTH = '550px';
  const STACK_SPACING = 1;

  // --- STATE FOR KEYWORD MENU ---
  const [anchorEl, setAnchorEl] = useState(null);
  const [keywordList, setKeywordList] = useState([]);
  const [isLoadingKeywords, setIsLoadingKeywords] = useState(false);

  // Track cursor position to insert keywords correctly
  const [cursorPos, setCursorPos] = useState(null);

  const inputRef = useRef(null);

  // --- HANDLERS ---

  const handleInputSelect = (e) => {
    setCursorPos(e.target.selectionStart);
  };

  const handleKeywordIconClick = async (event) => {
    setAnchorEl(event.currentTarget);
    setIsLoadingKeywords(true);
    const words = await actions.fetchUniqueKeywords();
    setKeywordList(words);
    setIsLoadingKeywords(false);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClearKeywords = () => {
    actions.handleFieldChange('keywords', '');
    setCursorPos(0);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeywordSelect = (word) => {
    const currentText = filterState.keywords || '';
    const insertAt =
      cursorPos !== null && cursorPos >= 0 && cursorPos <= currentText.length
        ? cursorPos
        : currentText.length;

    const before = currentText.substring(0, insertAt);
    const after = currentText.substring(insertAt);

    const textToInsert = `${word} `;
    const newText = `${before}${textToInsert}${after}`;

    actions.handleFieldChange('keywords', newText);

    const newPos = insertAt + textToInsert.length;
    setCursorPos(newPos);

    handleMenuClose();

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  // Base style for standard inputs
  const singleLineInputSx = {
    width: '100%',
    '& .MuiInputBase-root': {
      height: '30px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
    },
    '& .MuiInputBase-input': {
      padding: '0 8px',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiInputLabel-root': {
      top: '-5px',
    },
  };

  // --- Style for Orange Border Fields ---
  const infoFilterInputSx = {
    ...singleLineInputSx,
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#FF9900', // Default Orange
      },
      '&:hover fieldset': {
        borderColor: '#FF9900', // Hover Orange
        borderWidth: '2px',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#FF9900', // Focus Orange
        borderWidth: '2px',
      },
    },
  };

  const dateInputSx = {
    ...singleLineInputSx,
    width: '150px',
  };

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        width: '100%',
        height: '100%',
        bgcolor: '#fff',
        overflowY: 'auto',
      }}>
      <Typography variant='h6' fontWeight='bold' sx={{ mt: 0, mb: 1, fontSize: '1rem' }}>
        Filter Information
      </Typography>

      <Stack spacing={STACK_SPACING} sx={{ maxWidth: FIELD_WIDTH }}>
        {/* DATE RANGE */}
        <Box>
          <Typography variant='caption' sx={{ color: '#000000', fontWeight: 'bold' }}>
            Date Range
          </Typography>
          <RadioGroup
            row
            value={filterState.dateRangeType || 'ALL'}
            onChange={(e) => actions.handleFieldChange('dateRangeType', e.target.value)}
            sx={{ mt: -0.5 }}>
            <FormControlLabel
              value='ALL'
              control={<Radio size='small' sx={{ p: 0.5 }} />}
              label={<Typography variant='body2'>All</Typography>}
            />
            <FormControlLabel
              value='RANGE'
              control={<Radio size='small' sx={{ p: 0.5 }} />}
              label={<Typography variant='body2'>Calendar Range</Typography>}
            />
          </RadioGroup>

          {filterState.dateRangeType === 'RANGE' && (
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, ml: 1 }}>
              <TextField
                label='From'
                type='date'
                size='small'
                sx={dateInputSx}
                InputLabelProps={{ shrink: true }}
                value={filterState.dateFrom || ''}
                onChange={(e) => actions.handleFieldChange('dateFrom', e.target.value)}
              />
              <TextField
                label='To'
                type='date'
                size='small'
                sx={dateInputSx}
                InputLabelProps={{ shrink: true }}
                value={filterState.dateTo || ''}
                onChange={(e) => actions.handleFieldChange('dateTo', e.target.value)}
              />
            </Box>
          )}
        </Box>

        {/* --- AMENDED: SHOWCASE & BATCH ROW --- */}
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', width: '100%' }}>
          {/* Showcase */}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography
              sx={{
                color: '#000000',
                fontWeight: 'bold',
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                marginBottom: '0px',
                marginLeft: '2px',
                lineHeight: 1.2,
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}>
              Showcase
            </Typography>
            <Box
              sx={{
                borderRadius: '4px',
                minHeight: '26px',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                transition: 'background-color 0.3s ease',
                border: '1px solid #FF9900',
                width: '30px',
                padding: 0,
                justifyContent: 'center',
                backgroundColor: '#fff',
                cursor: 'pointer',
              }}
              onClick={() =>
                actions.handleFieldChange('showcase', !filterState.showcase)
              }>
              <Checkbox
                checked={!!filterState.showcase}
                size='small'
                sx={{
                  padding: 0,
                  '&.Mui-checked': { color: '#00008B' },
                  pointerEvents: 'none',
                }}
              />
            </Box>
          </Box>

          {/* AMENDED: Batch Pulldown - ONLY IF canUpdateMarkers */}
          {canUpdateMarkers && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                flexGrow: 1,
              }}>
              <Typography
                sx={{
                  color: '#000000',
                  fontWeight: 'bold',
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  marginBottom: '1px',
                  marginLeft: '2px',
                  lineHeight: 1.2,
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                }}>
                Batch
              </Typography>
              <FormControl size='small' fullWidth>
                <Select
                  fullWidth
                  value={filterState.unpublishedBatch || 'ALL'}
                  onChange={(e) =>
                    actions.handleFieldChange('unpublishedBatch', e.target.value)
                  }
                  MenuProps={{
                    sx: { zIndex: 100020 },
                  }}
                  sx={{
                    height: '26px',
                    fontSize: '0.75rem',
                    backgroundColor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FF9900',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FF9900',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FF9900',
                      borderWidth: '2px',
                    },
                  }}>
                  <MenuItem value='ALL' sx={{ fontSize: '0.75rem' }}>
                    All
                  </MenuItem>
                  {allBatches &&
                    allBatches.map((batch) => (
                      <MenuItem
                        key={batch.id}
                        value={batch.id}
                        sx={{ fontSize: '0.75rem' }}>
                        {batch.filename}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        {/* KEYWORDS */}
        <Box>
          <Typography variant='caption' sx={{ color: '#000000', fontWeight: 'bold' }}>
            Keywords
          </Typography>
          <TextField
            fullWidth
            variant='outlined'
            size='small'
            sx={infoFilterInputSx}
            placeholder='Enter keywords...'
            value={filterState.keywords || ''}
            onChange={(e) => actions.handleFieldChange('keywords', e.target.value)}
            inputRef={inputRef}
            onSelect={handleInputSelect}
            onClick={handleInputSelect}
            onKeyUp={handleInputSelect}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end' sx={{ gap: 0.5 }}>
                  <IconButton
                    onClick={handleKeywordIconClick}
                    size='small'
                    title='Select from existing keywords'
                    sx={{ padding: '2px' }}>
                    <HelpIcon sx={{ color: '#FF9900', fontSize: 20 }} />
                  </IconButton>

                  {filterState.keywords && (
                    <IconButton
                      onClick={handleClearKeywords}
                      size='small'
                      title='Clear keywords'
                      sx={{ padding: '2px' }}>
                      <CloseIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              style: { maxHeight: 300, width: '250px' },
            }}
            sx={{ zIndex: 100020 }}>
            {' '}
            {isLoadingKeywords ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : keywordList.length > 0 ? (
              keywordList.map((word, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleKeywordSelect(word)}
                  sx={{ fontSize: '0.875rem', py: 0.5 }}>
                  {word}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled sx={{ fontSize: '0.875rem' }}>
                No keywords found
              </MenuItem>
            )}
          </Menu>
        </Box>

        {/* CONTROLLER FIELD */}
        <Box>
          <Typography variant='caption' sx={{ color: '#000000', fontWeight: 'bold' }}>
            Controller
          </Typography>
          <TextField
            fullWidth
            variant='outlined'
            size='small'
            sx={infoFilterInputSx}
            placeholder='Enter controller name...'
            value={filterState.controller || ''}
            onChange={(e) => actions.handleFieldChange('controller', e.target.value)}
            InputProps={{
              endAdornment: filterState.controller ? (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={() => actions.handleFieldChange('controller', '')}
                    size='small'
                    title='Clear controller'
                    sx={{ padding: '2px' }}>
                    <CloseIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        {/* INFO FILTER */}
        {showInfoFilter && (
          <InfoFilterInputs
            filterState={filterState}
            onFieldChange={actions.handleFieldChange}
            showLabel={true}
          />
        )}
      </Stack>
    </Box>
  );
}
